import { React, useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatMonthYearLabelPtBr, formatShortMonthParam } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/generatorProjectUpdate.css';

const AtualizarProjetoGeradoras = () => {
    const [dateTimePeriodo, setDateTimePeriodo] = useState(null);
    const [errorPeriodo, setErrorPeriodo] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitAtualizarProjetoGeradoras (e) {
        e.preventDefault();

        if (!dateTimePeriodo) {
            setErrorPeriodo(true);
            return;
        }

        setErrorPeriodo(false);
        setIsSubmit(true);

        const periodo = formatShortMonthParam(dateTimePeriodo);
        const body = JSON.stringify({
            periodo
        });

        try {
            const response = await fetchFlask('updateProjectGenerators', body);
            setStatus(response.status);
            setMessage(response.message);
            setOpenAlerta(true);
        } finally {
            setIsSubmit(false);
        }
    };

    const periodoLabel = formatMonthYearLabelPtBr(dateTimePeriodo, 'Pendente');
    const periodoPayloadLabel = dateTimePeriodo ? formatShortMonthParam(dateTimePeriodo) : 'Não gerado';
    const completedFields = [dateTimePeriodo].filter(Boolean).length;

    return (
        <Box className='mainBox generator-project-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Atualizar Projeto UGs
            </PageHeader>

            <form action='' method='POST' id='formAtualizarProjetoGeradoras' onSubmit={submitAtualizarProjetoGeradoras} className='generator-project-layout'>
                <Box className='generator-project-workspace'>
                    <Box className='generator-project-intro'>
                        <Box>
                            <span className='generator-project-kicker'>Faturamento</span>
                            <h2>Ajuste de Projeto UGs</h2>
                        </Box>
                        <p>
                            Selecione a competência para sincronizar o projeto das UGs no ERP.
                        </p>
                    </Box>

                    <Box className='generator-project-panel'>
                        <Box className='generator-project-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Competência</span>
                                <strong>Período de referência</strong>
                            </Box>
                        </Box>

                        <Box className='generator-project-period-strip'>
                            <SyncAltRoundedIcon />
                            <FormControl className='generator-project-date-control' error={errorPeriodo}>
                                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        id='data'
                                        label='Período'
                                        views={['month', 'year']}
                                        openTo='month'
                                        minDate={new Date('2023-01-01')}
                                        maxDate={new Date()}
                                        value={dateTimePeriodo}
                                        onChange={(value) => {
                                            setDateTimePeriodo(value);
                                            setErrorPeriodo(false);
                                        }}
                                        slotProps={{
                                            openPickerButton: {
                                                color: 'primary'
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {errorPeriodo && (
                                    <FormHelperText>Por favor selecione um período.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='generator-project-impact'>
                        <AutorenewRoundedIcon />
                        <Box>
                            <strong>Sincronização de projeto</strong>
                            <span>As unidades geradoras serão atualizadas com a competência selecionada para manter o faturamento aderente ao ERP.</span>
                        </Box>
                    </Box>

                    <Box className='generator-project-rail'>
                        <Box>
                            <SolarPowerRoundedIcon />
                            <span>Escopo</span>
                            <strong>UGs geradoras</strong>
                        </Box>
                        <Box>
                            <HubRoundedIcon />
                            <span>Destino</span>
                            <strong>Projeto ERP</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='generator-project-summary'>
                    <Box className='generator-project-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da atualização</strong>
                        </Box>
                    </Box>

                    <Box className='generator-project-summary-list'>
                        <Box>
                            <span>Competência</span>
                            <strong className={!dateTimePeriodo ? 'is-empty' : undefined}>{periodoLabel}</strong>
                        </Box>
                        <Box>
                            <span>Valor enviado</span>
                            <strong className={!dateTimePeriodo ? 'is-empty' : undefined}>{periodoPayloadLabel}</strong>
                        </Box>
                        <Box>
                            <span>Operação</span>
                            <strong>Atualização de projeto</strong>
                        </Box>
                    </Box>

                    <Box className='generator-project-attention'>
                        <AutorenewRoundedIcon />
                        <p>
                            Revise a competência antes de confirmar. A rotina atualizará os projetos das geradoras para o período informado.
                        </p>
                    </Box>

                    <Box className='generator-project-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/1 preenchido</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<AutorenewRoundedIcon />}>
                        Atualizar Projetos
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AtualizarProjetoGeradoras;
